import { Client } from "@/types/client";
import { PaginatedResponse } from "@/types/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createClient, deleteClient, payClientCredit, updateClient } from "./api";

export function useCreateClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: Client) => createClient(values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
    },
  });
}

export function useUpdateClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: Client) => updateClient(values),

    onSuccess: (_response, values) => {
      // 1️⃣ obtener todas las queries relacionadas con "clients"
      const queries = queryClient.getQueriesData<PaginatedResponse<Client>>({
        queryKey: ["clients"],
      });

      // 2️⃣ iterar cada cache (paginaciones, filtros, etc.)
      queries.forEach(([queryKey, oldData]) => {
        if (!oldData) return;

        // 3️⃣ reemplazar el usuario si coincide el id
        const updatedItems = oldData.items.map((item) =>
          item.id === values.id ? { ...item, ...values } : item,
        );

        // 4️⃣ actualizar cache
        queryClient.setQueryData(queryKey, {
          ...oldData,
          items: updatedItems,
        });
      });
    },
  });
}

export function useDeleteClient() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: Client["id"]) => deleteClient(id),

    onSuccess: (_response, id) => {
      const queries = queryClient.getQueriesData<PaginatedResponse<Client>>({
        queryKey: ["clients"],
      });

      queries.forEach(([queryKey, oldData]) => {
        if (!oldData) return;

        const updatedItems = oldData.items.filter((client) => client.id !== id);

        queryClient.setQueryData(queryKey, {
          ...oldData,
          items: updatedItems,
        });
      });
    },
  });
}

export function usePayClientCredit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      customer_id,
      payload,
    }: {
      customer_id: Client["id"];
      payload: { payment_ids: string[]; invoice_number: string };
    }) => payClientCredit(customer_id, payload),

    onSuccess: (_response, { customer_id }) => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["client_purchases", customer_id] });
      queryClient.invalidateQueries({ queryKey: ["payments"] });
    },
  });
}
